// navigation/nav-config.ts
// Single source of truth for the Core Nav: feature ids, the route-id each maps
// to, section grouping, lucide icons, and the per-feature lock-screen copy.
// Visual values live in the components; this file is data only.

import {
  Radio, GitBranch, Aperture, Wrench, FlaskConical, Layers,
  BarChart4, Target, FileText, Sparkles, Rocket, Workflow,
  History, Settings, Users, Zap, Tag, KeyRound,
  type LucideIcon,
} from "lucide-react";

// Must match the backend FEATURE_IDS / frontend entitlements map.
export type FeatureId =
  | "brand-monitoring" | "ai-reasoning-path" | "deep-signal"
  | "action-items" | "test-refine" | "brand-controls"
  | "performance" | "prompt-intelligence" | "insights-reports"
  | "upmode-agent" | "brand-publisher" | "ai-workflows";

export type SectionId = "measure" | "optimize" | "track" | "advanced";

export interface NavSubItem {
  /** Route id passed to onNavigate. */
  page: string;
  label: string;
  icon: LucideIcon;
}

export interface NavFeature {
  id: FeatureId;
  label: string;
  icon: LucideIcon;
  /** Route id passed to onNavigate (the app's page string). */
  page: string;
  section: SectionId;
  /** Extra route ids that should also mark this item active. */
  altPages?: string[];
  /** Nested items revealed when this item is active/expanded. */
  children?: NavSubItem[];
  /** When set, the row shows a coral count badge sourced at runtime. */
  badge?: "runCount";
}

export interface NavSectionDef {
  id: SectionId;
  label: string;
  collapsible?: boolean;
}

export const SECTIONS: NavSectionDef[] = [
  { id: "measure",  label: "Measure" },
  { id: "optimize", label: "Optimize" },
  { id: "track",    label: "Track" },
  { id: "advanced", label: "Advanced", collapsible: true },
];

// Array order IS nav order. The rail renders the five core modules flat (no
// section header) because the Product Focus order deliberately crosses the old
// measure/optimize/track grouping, then closes with a collapsible "Advanced"
// group holding everything else. `section` is retained for the command palette
// hint and the admin feature-access panel, which still group by it.
export const FEATURES: NavFeature[] = [
  // ── The five core modules, in product order ──────────────────────────────
  { id: "brand-monitoring",   label: "Brand Monitoring",   icon: Radio,       page: "dashboard",          section: "measure" },
  { id: "action-items",       label: "Action Items",       icon: Wrench,      page: "fix-actions",        section: "optimize" },
  { id: "performance",        label: "Performance",        icon: BarChart4,   page: "analytics-overview", section: "track" },
  { id: "ai-reasoning-path",  label: "AI Reasoning Path",  icon: GitBranch,   page: "cognition",          section: "measure" },
  { id: "deep-signal",        label: "Deep Signal",        icon: Aperture,    page: "deep-signal",        section: "measure" },

  // ── The Advanced group, collapsed by default. Order is fixed by the
  //    UPM-140 handoff and supersedes the Phase 5 ordering. ────────────────
  { id: "test-refine",        label: "Test & Refine",      icon: FlaskConical, page: "ai-sandbox",        section: "optimize" },
  { id: "brand-controls",     label: "Brand Controls",     icon: Layers,      page: "brand-tools",        section: "optimize" },
  { id: "prompt-intelligence", label: "Prompt Intelligence", icon: Target,    page: "prompt-tracking",    section: "track" },
  { id: "insights-reports",   label: "Insights & Reports", icon: FileText,    page: "report-generator",   section: "track" },
  {
    id: "upmode-agent", label: "Upmode Agent", icon: Sparkles,
    page: "upmode-agent", section: "advanced",
  },
  {
    id: "brand-publisher", label: "Brand Publisher", icon: Rocket,
    page: "brand-publisher", section: "advanced",
    children: [{ page: "publication-history", label: "Publication History", icon: History }],
  },
  {
    id: "ai-workflows", label: "AI Workflows", icon: Workflow,
    page: "agents", section: "advanced",
    children: [{ page: "agent-runs", label: "Workflow History", icon: History }],
  },
];

/** The two labeled groups the rail renders the core five in (UPM-140). */
export const RAIL_GROUPS: { label: string; ids: FeatureId[] }[] = [
  { label: "Operate",   ids: ["brand-monitoring", "action-items", "performance"] },
  { label: "Interpret", ids: ["ai-reasoning-path", "deep-signal"] },
];

/** Advanced, in handoff order. Derived so it can never drift from FEATURES. */
export const ADVANCED_IDS: FeatureId[] = [
  "test-refine", "brand-controls", "prompt-intelligence",
  "insights-reports", "upmode-agent", "brand-publisher", "ai-workflows",
];

/** The five core modules, mirrored from the backend PRODUCT_FOCUS_LIVE. They
 *  render flat at the top of the rail; everything else falls into Advanced. */
export const CORE_MODULE_IDS: FeatureId[] = [
  "brand-monitoring",
  "action-items",
  "performance",
  "ai-reasoning-path",
  "deep-signal",
];

/** Nav grouping only. Membership here says nothing about entitlement: an
 *  Advanced module still resolves live/locked/hidden per user like any other. */
export function isCoreModule(id: FeatureId): boolean {
  return CORE_MODULE_IDS.includes(id);
}

/** Label for the collapsible group that closes the rail. */
export const ADVANCED_GROUP_LABEL = "Advanced";

// Admin / footer items (role-gated as today, not part of the locks model).
// Product Focus trims Manage to Settings + User Management. Integrations,
// Brand Access and Brand API Keys are commented out rather than deleted: the
// routes and screens are untouched, so restoring a row is a one-line revert.
export interface ManageItem { page: string; label: string; icon: LucideIcon; adminOnly?: boolean; agentOnly?: boolean; }
// The rail's Manage menu (UPM-140) lists all five. Role gating is unchanged:
// adminOnly / agentOnly rows are filtered per seat exactly as before.
export const MANAGE_ITEMS: ManageItem[] = [
  { page: "settings",            label: "Settings",        icon: Settings },
  { page: "integrations",        label: "Integrations",    icon: Zap,   agentOnly: true },
  { page: "admin-users",         label: "User Management", icon: Users, adminOnly: true },
  { page: "admin-brand-access",  label: "Brand Access",    icon: Tag,   adminOnly: true },
  { page: "admin-brand-keys",    label: "Brand API Keys",  icon: KeyRound, adminOnly: true },
];

/** Which feature (if any) a given route id belongs to — drives active state. */
export function featureForPage(page: string): NavFeature | undefined {
  return FEATURES.find(
    (f) => f.page === page
      || f.altPages?.includes(page)
      || f.children?.some((c) => c.page === page),
  );
}

// ── Lock-screen content ────────────────────────────────────────────────────
// Authored per feature: what it does and why it's worth unlocking. Shown in the
// LockedFeatureModal. Copy can be refined later; the structure stays.
export interface LockScreen {
  title: string;
  /** One-line value proposition under the title. */
  desc: string;
  /** Optional short bullets of what they get. */
  highlights?: string[];
}

export const LOCK_SCREENS: Record<FeatureId, LockScreen> = {
  "brand-monitoring": {
    title: "Brand Monitoring",
    desc: "See exactly how the major AI models describe your brand, scored and tracked over time so you know where you stand.",
    highlights: ["Per-model perception scores", "Tracked run over run", "The source quotes behind every number"],
  },
  "ai-reasoning-path": {
    title: "AI Reasoning Path",
    desc: "Trace how each model reasons its way to an answer about you, so you can see which sources and associations shape its view.",
    highlights: ["Step-by-step model reasoning", "The associations driving perception", "Where to intervene for the biggest shift"],
  },
  "deep-signal": {
    title: "Deep Signal",
    desc: "Go beneath the score to the strongest signals in your data, the standout quotes, framings, and gaps worth acting on.",
    highlights: ["The signals that move perception most", "Framing and sentiment detail", "Flagged factual gaps to close"],
  },
  "action-items": {
    title: "Action Items",
    desc: "A prioritized, impact-ranked list of the highest-leverage fixes, so you always know what to tackle next.",
    highlights: ["Ranked by likely impact", "Grounded in your latest run", "Clear next step on each item"],
  },
  "test-refine": {
    title: "Test & Refine",
    desc: "Try a messaging change in a sandbox and see how the models would respond before you commit to it.",
    highlights: ["Safe what-if scenarios", "Compare before and after", "Refine messaging with evidence"],
  },
  "brand-controls": {
    title: "Brand Controls",
    desc: "Set the brand facts, voice, and guardrails that keep every analysis and output true to who you are.",
    highlights: ["Canonical brand facts", "Voice and tone guardrails", "Consistent results across the app"],
  },
  "performance": {
    title: "Performance",
    desc: "Watch your AI perception move over time, with the trends and inflection points that matter for the business.",
    highlights: ["Perception trends over time", "What is improving and what is not", "Leadership-ready views"],
  },
  "prompt-intelligence": {
    title: "Prompt Intelligence",
    desc: "Understand which prompts surface your brand, where you show up early, and where there is room to grow.",
    highlights: ["Coverage across real prompts", "Where you appear and where you don't", "Prompts worth adding"],
  },
  "insights-reports": {
    title: "Insights & Reports",
    desc: "Turn your data into a clear story, saved insights and polished reports you can share with the team or the board.",
    highlights: ["Save insights as you find them", "Board-ready reports", "Share with a link"],
  },
  "upmode-agent": {
    title: "Upmode Agent",
    desc: "Your always-on AI analyst. Ask anything about your brand's AI visibility and have it take action, right inside the app.",
    highlights: ["Ask about anything in your data", "Grounded, cited answers", "Takes action, not just talk"],
  },
  "brand-publisher": {
    title: "Brand Publisher",
    desc: "Push content updates straight to your channels and watch the models re-index, closing the loop from insight to published action.",
    highlights: ["Publish to your channels", "Watch models re-index", "Insight to action in one place"],
  },
  "ai-workflows": {
    title: "AI Workflows",
    desc: "Automate the probe, measure, act loop. Set triggers, chain actions, and let Upmode run interventions on a schedule.",
    highlights: ["Trigger-based automation", "Chain multi-step actions", "Runs on your schedule"],
  },
};
